// your-select.jsx
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid'
import * as SelectPrimitive from '@radix-ui/react-select'
import cx from 'classnames'
import { motion } from 'framer-motion'
import React from 'react'
import { Card } from './Card'

const variantPropsBySide = {
  top: { scaleY: 0.8, scaleX: 0.9, translateY: '10%' },
  bottom: { scaleY: 0.8, scaleX: 0.9, translateY: '-10%' },
  left: { scaleX: 0.8, scaleY: 0.9, translateX: '10%' },
  right: { scaleX: 0.8, scaleY: 0.9, translateX: '-10%' },
}

type SelectProps = React.ComponentProps<typeof SelectPrimitive.Root> &
  Pick<React.ComponentProps<typeof SelectPrimitive.Content>, 'side' | 'sideOffset' | 'align' | 'alignOffset'> & {
    label?: React.ReactNode
    placeholder?: string
  }
export const Select = React.forwardRef<HTMLButtonElement, SelectProps>(
  ({ children, label, placeholder, side, sideOffset, align, alignOffset, ...props }, forwardedRef) => {
    const contentProps = { side, sideOffset, align, alignOffset }

    return (
      <div className="flex flex-col gap-1">
        <span className="text-sm text-gray-10">{label}</span>
        <SelectPrimitive.Root {...props}>
          <SelectPrimitive.Trigger
            className="flex h-6 w-fit min-w-[8rem] items-center justify-between gap-1 rounded bg-gray-1 px-3 py-4 text-sm radix-placeholder:text-gray-10"
            ref={forwardedRef}
            asChild
          >
            <Card>
              <SelectPrimitive.Value placeholder={placeholder} />
              <SelectPrimitive.Icon>
                <ChevronDownIcon className="h-5 w-5" />
              </SelectPrimitive.Icon>
            </Card>
          </SelectPrimitive.Trigger>
          <SelectPrimitive.Portal>
            <SelectPrimitive.Content className="min-w-[8rem]" position="popper" {...contentProps} asChild>
              <motion.div
                variants={{
                  open: { opacity: 1, scaleY: 1, scaleX: 1, translateY: '0', translateX: '0' },
                  closed: {
                    opacity: 0,
                    ...variantPropsBySide[side ?? 'bottom'],
                  },
                }}
                initial="closed"
                animate="open"
                exit="closed"
                transition={{ ease: 'easeInOut', duration: 0.25 }}
                className={cx('rounded-md bg-gray-12 p-1 text-gray-1')}
              >
                <SelectPrimitive.ScrollUpButton>
                  <ChevronUpIcon />
                </SelectPrimitive.ScrollUpButton>
                <SelectPrimitive.Viewport>{children}</SelectPrimitive.Viewport>
                <SelectPrimitive.ScrollDownButton>
                  <ChevronDownIcon />
                </SelectPrimitive.ScrollDownButton>
              </motion.div>
            </SelectPrimitive.Content>
          </SelectPrimitive.Portal>
        </SelectPrimitive.Root>
      </div>
    )
  }
)

type SelectItemProps = React.ComponentProps<typeof SelectPrimitive.Item>
export const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(({ children, ...props }, forwardedRef) => {
  return (
    <SelectPrimitive.Item
      {...props}
      className={cx(
        'flex cursor-default justify-between rounded-md px-4 py-2 text-xs font-normal radix-highlighted:bg-gray-10 radix-state-checked:bg-gray-9',
        props.className
      )}
      ref={forwardedRef}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
})
